import { equals } from 'ramda';
import type { TDispatch } from '../index';
import { getStore } from '../storeRef';
import { actions as actionsF } from '../form';

import {
  createNewCommit,
  createNewDocument,
  deleteExistingCommit,
  deleteExistingDocument,
  updateCommitAndClose,
  updateExistingCommit,
  changeFieldInDocument,
  putNewDocument,
  getDocument,
  deleteFile,
  postFile,
  patchFileMetadata,
  postFileContent,
} from './helpers/apiFetchers';

import { fetchWithOptions } from 'Utility/fetch';
import { getJWTData, parseJWTFromCookie } from 'Utility/jwtAuthTools';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';
import {
  filterDiagnoses,
  filterLForceDocs,
  filterMeasurements,
  filterRemovedDocuments,
  filterUsableDocuments,
} from './helpers/documentFilters';
import { sortAndMergeDocuments } from 'Utility/documentHandling';
import { createAction } from '@reduxjs/toolkit';

/* Action types */
const LOAD = 'neuro-ui/documents/LOAD';
const RECEIVE = 'neuro-ui/documents/RECEIVE';
const RECEIVE_REMOVED = 'neuro-ui/documents/RECEIVE_REMOVED';
const CLEAR = 'neuro-ui/documents/CLEAR';
const NEW_DOCUMENT = 'neuro-ui/documents/NEW_DOCUMENT';
const DELETE_DOCUMENT = 'neuro-ui/documents/DELETE_DOCUMENT';
const UNDELETE_DOCUMENT = 'neuro-ui/documents/UNDELETE_DOCUMENT';
const NEW_COMMIT = 'neuro-ui/documents/NEW_COMMIT';
const UPDATE_COMMIT = 'neuro-ui/documents/UPDATE_COMMIT';
const UPDATE_DOCUMENT = 'neuro-ui/documents/UPDATE_DOCUMENT';
const CANCEL_COMMIT = 'neuro-ui/documents/CANCEL_COMMIT';
const MODIFY_FIELD = 'neuro-ui/documents/MODIFY_FIELD';
const MODIFY_LOCKED_FIELD = 'neuro-ui/documents/MODIFY_LOCKED_FIELD';
const SET_NEW_DOC = 'neuro-ui/documents/SET_NEW_DOC';
const UNSET_NEW_DOC = 'neuro-ui/documents/UNSET_NEW_DOC';
const LOAD_FILES_METADATA = 'neuro-ui/documents/LOAD_FILES_METADATA';
const NEW_FILE_METADATA = 'neuro-ui/documents/NEW_FILE_METADATA';
const EDIT_FILE_METADATA = 'neuro-ui/documents/EDIT_FILE_METADATA';
const REMOVE_FILE_METADATA = 'neuro-ui/documents/REMOVE_FILE_METADATA';

// Actions for document loading. Called when page is opened or refreshed
const loadAction = createAction(LOAD, () => ({ payload: null }));

const receiveAction = createAction(
  RECEIVE,
  (documents: INeuroDocument[], sortedAndMergedDocuments: Array<IDocumentV2>) => {
    return { payload: { documents, sortedAndMergedDocuments } };
  },
);

const receiveRemovedAction = createAction(RECEIVE_REMOVED, (removedDocuments: INeuroDocument[]) => {
  return { payload: { removedDocuments } };
});

const clearAction = createAction(CLEAR, (documents: INeuroDocument[] = []) => {
  return { payload: { documents } };
});

const newDocumentAction = createAction(NEW_DOCUMENT, (data: Partial<INeuroDocument>) => {
  return { payload: { data } };
});

const deleteDocumentAction = createAction(
  DELETE_DOCUMENT,
  (documentId: string, removeInfo: INeuroDocument['removeInfo']) => {
    return { payload: { documentId, removeInfo } };
  },
);
const undeleteDocumentAction = createAction(
  UNDELETE_DOCUMENT,
  (documentId: string, restoreTS: number, userId: string, userOrg: string) => {
    return {
      payload: { documentId, restoreTS, userId, userOrg },
    };
  },
);
const newCommitAction = createAction(NEW_COMMIT, (id: string, data: INeuroCommit) => {
  return { payload: { id, data } };
});

const updateCommitAction = createAction(
  UPDATE_COMMIT,
  (id: string, cid: string, close: boolean, data: Record<string | number, unknown>) => {
    return { payload: { id, cid, close, data } };
  },
);

const updateDocumentAction = createAction(UPDATE_DOCUMENT, (document: INeuroDocument) => {
  return { payload: document };
});

const cancelCommitAction = createAction(CANCEL_COMMIT, (id: string, cid: string) => {
  return { payload: { id, cid } };
});

// Change a field in a document
const modifyFieldAction = createAction(MODIFY_FIELD, (id: string, commit: INeuroCommit) => {
  return { payload: { id, commit } };
});

// Change a field in a locked document
const modifyLockedFieldAction = createAction(MODIFY_LOCKED_FIELD, (id: string, commit: INeuroCommit) => {
  return { payload: { id, commit } };
});

// Set new document being added information so that spinners know to spin inside new and edit buttons
const setNewDocAction = createAction(SET_NEW_DOC, (name: string, id: string) => {
  return { payload: { name, id } };
});

const unsetNewDocAction = createAction(UNSET_NEW_DOC, () => {
  return { payload: null };
});

const loadFilesMetadataAction = createAction(LOAD_FILES_METADATA, (files: Array<INeuroDocument>) => {
  const mergedFiles = sortAndMergeDocuments(files) as unknown as Array<IDocumentWithTypeV2<'filestorefile'>>;
  return {
    payload: { files, mergedFiles },
  };
});

const newFileMetadataAction = createAction(NEW_FILE_METADATA, (file: INeuroDocument) => {
  const mergedFile = sortAndMergeDocuments([file])[0] as unknown as IDocumentWithTypeV2<'filestorefile'>;
  return {
    payload: { file, mergedFile },
  };
});

const editFileMetadataAction = createAction(EDIT_FILE_METADATA, (documentId: string, file: INeuroDocument) => {
  const mergedFile = sortAndMergeDocuments([file])[0] as unknown as IDocumentWithTypeV2<'filestorefile'>;

  return {
    payload: { documentId, file, mergedFile },
  };
});

const removeFileMetadataAction = createAction(
  REMOVE_FILE_METADATA,
  (
    id: string,
    reason: { date: number; reason: { date: PartialDate; reason: string; reasonOther?: string } },
    userId: string,
    orgId: string,
  ) => {
    return {
      payload: { id, reason, userId, orgId },
    };
  },
);

/**
 * Load all documents from server
 */
const fetchDocumentData =
  (firstFetch = true) =>
  (dispatch: TDispatch): Promise<boolean | undefined> => {
    firstFetch && dispatch(loadAction());

    return fetchWithOptions(`/api/documents`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
      .then(
        (res: Response) => {
          if (res.status === 200) {
            return res.json();
          } else {
            throw new Error('unexpected status ' + res.status);
          }
        },
        (error: unknown) => {
          throw error;
        },
      )
      .then(json => {
        const jwt = getJWTData();
        if (!jwt) return;

        let documents: INeuroDocument[] = [];
        let removedDocuments: INeuroDocument[] = [];
        let files: INeuroDocument[] = [];

        // Filter usable documents for the current user
        documents = filterUsableDocuments(jwt, json);

        // Create an array of sorted and merged documents
        let sortedAndMergedDocuments = sortAndMergeDocuments(documents);

        // Filter diagnosis documents
        documents = filterDiagnoses(documents, sortedAndMergedDocuments);

        // Filter measurement docs
        documents = filterMeasurements(documents, sortedAndMergedDocuments);

        // Filter documents from L-Force integration
        documents = filterLForceDocs(documents, sortedAndMergedDocuments);

        // Separate removed docs
        const separatedDocuments = filterRemovedDocuments(documents);
        documents = separatedDocuments[0];
        removedDocuments = separatedDocuments[1];

        // Separate files
        files = documents.filter(d => d.documentType === 'filestorefile');
        documents = documents.filter(d => d.documentType !== 'filestorefile');

        // Create a Set of documentIds of filtered documents
        const documentIdsSet = new Set(documents.map(d => d.documentId));

        // Filter sortedAndMergedDocuments to match filtered documents
        sortedAndMergedDocuments = sortedAndMergedDocuments.filter(d => documentIdsSet.has(d._id));

        dispatch(actionsF.loadEditingDocuments(documents));
        dispatch(receiveAction(documents, sortedAndMergedDocuments));

        dispatch(receiveRemovedAction(removedDocuments));

        dispatch(loadFilesMetadataAction(files));
        return true;
      })
      .catch((err: unknown) => {
        makeLog(
          Loggable.BugActionable,
          err instanceof Error ? err : new Error('strange error'),
          new ActionableErrorContext(null, 'GET documents'),
        );
        return false;
      });
  };

/** Create a new document (when form handler receives no id ) */
const createDocument =
  (
    name: string,
    // Whether to not to update form editingDocuments with the new document (default false, aka update them). 'true' could be used for sub documents
    noFormUpdate = false,
    initialFormData?: TAnyObject & IControlProps,
  ) =>
  async (dispatch: TDispatch): Promise<TCreationPromiseType> => {
    // Create new document (and commit)
    dispatch(setNewDocAction(name, 'noid'));
    const ids = await createNewDocument(name, dispatch, noFormUpdate, initialFormData);
    dispatch(unsetNewDocAction());
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return ids ?? 'Document creation failed';
  };

/**
 * Put a new document with data
 *
 */
const putDocument =
  (name: TDocumentType, data: TAnyObject & Partial<IControlProps>) =>
  async (dispatch: TDispatch): Promise<TCreationPromiseType> => {
    // Create new document (and commit)
    dispatch(setNewDocAction(name, 'noid'));
    const ids = await putNewDocument(name, dispatch, data);
    dispatch(unsetNewDocAction());
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return ids ?? 'Document creation failed';
  };

const createCommit =
  (editingDocument: { name: string; id: string }, oldData?: TAnyObject & IControlProps) =>
  async (dispatch: TDispatch): Promise<TCreationPromiseType> => {
    const {
      documents: { documents: allDocuments },
      session,
    } = getStore().getState();

    // Check if the document exists
    const curDocument = allDocuments ? (allDocuments.find(d => d.documentId === editingDocument.id) ?? null) : null;
    const openCommit =
      curDocument?.commits.find(c => c.commitDate === null && c.creatorId === session.data?.useruuid) ?? null;

    if (openCommit) return { _cid: openCommit.commitId };
    else {
      // Create new commit
      dispatch(setNewDocAction(editingDocument.name, editingDocument.id));
      const cid = await createNewCommit(editingDocument, dispatch, oldData);
      dispatch(unsetNewDocAction());
      return cid ? { _cid: cid } : 'Commit creation failed';
    }
  };

/**
 * Update document data
 */
const updateDocumentData =
  (editingDocument: { name: string; id: string }, formData?: TAnyObject, signal?: AbortSignal) =>
  async (dispatch: TDispatch): Promise<TCreationPromiseType> => {
    const {
      documents: { documents: allDocuments },
      session,
      form,
    } = getStore().getState();

    // Check if the document exists
    const curDocument = allDocuments?.find(d => d.documentId === editingDocument.id) ?? null;
    // Check if an open commit exists
    const openCommit =
      curDocument?.commits.find(c => c.commitDate === null && c.creatorId === session.data?.useruuid) ?? null;

    if (openCommit) {
      const commitId = openCommit.commitId;
      // Document and commit ok
      // Get formData from props, or from state
      formData = formData ?? (form.formData[editingDocument.id] as any);

      // Only update if data has changed
      if (!equals(formData, openCommit.data)) {
        const updateMessage = await updateExistingCommit(editingDocument, commitId, dispatch, formData as any, signal);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        return updateMessage ?? 'Could not update data';
      }
      return 'No data to update';
    } else return 'No open commits';
  };

/**
 * Finish updating document/commit
 */
const closeCommit =
  (
    editingDocument: { name: string; id: string },
    formData: TAnyObject,
    subDocuments?: { name: string; id: string }[],
  ) =>
  async (dispatch: TDispatch): Promise<void> => {
    const {
      documents: { documents: allDocuments = [] },
      session,
      form,
    } = getStore().getState();

    // Document currently being processed
    const curDocument = (doc: { name: string; id: string }) => allDocuments.find(d => d.documentId === doc.id);

    const handleClose = async (doc?: INeuroDocument, thisFormData?: unknown): Promise<void> => {
      if (doc) {
        const openCommit = doc.commits.find(c => c.commitDate === null && c.creatorId === session.data?.useruuid);
        if (openCommit) {
          const { commitId } = openCommit;
          // Document and commit ok
          // Get formData from props, or from state
          formData = thisFormData ?? (form.formData[doc.documentId] as any);
          await updateCommitAndClose(
            { name: doc.documentType, id: doc.documentId },
            commitId,
            dispatch,
            formData as any,
          );
        }
      }
    };

    // Close main document
    await handleClose(curDocument(editingDocument), formData);

    // Close subDocuments
    if (subDocuments && subDocuments.length > 0) {
      await Promise.all(
        subDocuments.map(async s => {
          const thisDoc = curDocument(s);
          // Get data for this document, since formData is not available for sub documents
          const thisData: unknown = thisDoc?.commits.find(c => c.commitDate === null)?.data;
          await handleClose(thisDoc, thisData);
        }),
      );
    }
  };

/**
 * Find open commit by document Id and delete it if it isn't the only commit, else delete document.
 */
const deleteCommit =
  (editingDocument: { name: string; id: string }, subDocuments?: { name: string; id: string }[]) =>
  async (dispatch: TDispatch): Promise<void> => {
    const {
      session,
      documents: { documents: allDocuments = [] },
    } = getStore().getState();

    const currentUser = session.data?.useruuid;
    const currentOrg = session.data?.orgid;

    // const allDocuments = (path(['documents', 'documents'], getState()) || []) as INeuroDocument[];

    // Document currently being processed
    const curDocument = (doc: { name: string; id: string }) => allDocuments.find(d => d.documentId === doc.id);

    const handleDelete = async (doc?: INeuroDocument): Promise<void> => {
      // If only one commit exists, then delete the whole document
      if (doc && doc.commits.length <= 1) {
        const reason = { reason: 'Document creation cancelled.' };
        await deleteExistingDocument(
          { name: doc.documentType, id: doc.documentId },
          dispatch,
          reason,
          true, // Force removal of commits
          currentUser ?? '',
          currentOrg ?? '',
        );
      } else {
        const openCommit =
          doc && currentUser ? doc.commits.find(c => c.commitDate === null && c.creatorId === currentUser) : undefined;
        const commitId = openCommit ? openCommit.commitId : undefined;
        if (doc && commitId) {
          // Delete the open commit
          await deleteExistingCommit({ name: doc.documentType, id: doc.documentId }, commitId, dispatch);
        }
      }
    };

    // Delete main documents open commit
    await handleDelete(curDocument(editingDocument));

    // Delete possible subdocuments open commits
    if (subDocuments && subDocuments.length > 0) {
      await Promise.all(subDocuments.map(async s => await handleDelete(curDocument(s))));
    }
  };

/**
 * Delete document based on name and id, supply with reason.
 */
const deleteDocument =
  (
    editingDocument: { name: string; id: string },
    reason: { [key: string]: string | PartialDate },
    subDocs?: Array<{ name: string; id: string } | undefined>, // Sub documents that should be deleted as well
  ) =>
  async (dispatch: TDispatch): Promise<void> => {
    const {
      documents: { documents: allDocuments = [] },
    } = getStore().getState();
    const { session } = getStore().getState();

    const currentUser = session.data?.useruuid;
    const currentOrg = session.data?.orgid;

    const curDocument = allDocuments.find(d => d.documentId === editingDocument.id) ?? null;

    // Check if document exists
    if (curDocument) {
      // Filter this document id from allDocuments, which is later saved to redux state if delete goes through
      // Filter it here to avoid concurrency problems with subDocs
      await deleteExistingDocument(editingDocument, dispatch, reason, false, currentUser ?? '', currentOrg ?? '');
    }
    if (subDocs) {
      const deletePromises: Array<Promise<unknown>> = [];
      subDocs.forEach(d => {
        if (d?.id && d.name) {
          deletePromises.push(deleteExistingDocument(d, dispatch, reason, true, currentUser ?? '', currentOrg ?? ''));
        }
      });
      await Promise.all(deletePromises);
    }
  };

/**
 * Change a field in a (locked) document
 */
const modifyField =
  (
    editingDocument: { name: string; id: string },
    formData: any,
    newData: { [key: string]: any },
    fieldLocked = false, // This only changes which action name redux uses
    collectionInfo?: { id: string; idFieldName: string }, // Currently not possible to use this with multiple fields in new data
  ) =>
  async (dispatch: TDispatch) => {
    const allDocuments = getStore().getState().documents.documents;

    const curDocument = allDocuments?.find(d => d.documentId === editingDocument.id) ?? null;
    // Check if document exists
    if (curDocument) {
      return changeFieldInDocument(editingDocument, dispatch, formData, newData, fieldLocked, collectionInfo)?.then(
        res => {
          return res;
        },
      );
    } else return false;
  };

const updateDocumentFromServer =
  (editingDocument: { name: string; id: string }, updatePurpose: string) =>
  async (dispatch: TDispatch): Promise<boolean> => {
    return getDocument(editingDocument.name, editingDocument.id, updatePurpose).then(res => {
      if (res) {
        dispatch(updateDocumentAction(res));
        return true;
      }
      return false;
    });
  };

const postNewFile =
  (file: File, name: string, date: string, sub_type: string, reference_type?: string) => (dispatch: TDispatch) => {
    return postFile(file, name, date, sub_type, reference_type).then(res => {
      if (res) {
        dispatch(newFileMetadataAction(res));
        return true;
      }
      return false;
    });
  };

const editFileMetadata =
  (documentId: string, data: Partial<Pick<TFILE_METADATA, 'date' | 'name' | 'subType' | 'referenceType'>>) =>
  (dispatch: TDispatch) => {
    return patchFileMetadata(documentId, data).then(res => {
      if (res) {
        dispatch(editFileMetadataAction(documentId, res));
        return true;
      }
      return false;
    });
  };

const editFileContent = (file: File, documentId: string) => (dispatch: TDispatch) => {
  return postFileContent(file, documentId).then(res => {
    if (res) {
      dispatch(editFileMetadataAction(documentId, res));
      return true;
    }
    return false;
  });
};

const deleteFileData =
  (id: string, reason: { date: PartialDate; reason: string; reasonOther?: string }) => (dispatch: TDispatch) => {
    const getState = getStore().getState;
    const uid = getState().session.data?.useruuid;
    const oid = getState().session.data?.orgid;
    return deleteFile(id, reason).then(res => {
      if (res) {
        dispatch(removeFileMetadataAction(id, res, uid ?? '', oid ?? ''));
        return true;
      }
      return false;
    });
  };

export type TCreationPromiseType = { _id?: string; _cid?: string } | string;

/**
 * Actions that do some document data manipulation aside from just updating the redux store
 */
export const actions = {
  fetchDocumentData,
  createDocument,
  putDocument,
  createCommit,
  updateDocumentData,
  updateDocumentFromServer,
  closeCommit,
  deleteCommit,
  deleteDocument,
  modifyField,
  postNewFile,
  editFileMetadata,
  editFileContent,
  deleteFileData,
};

/**
 * Actions that exist only to update redux store
 */
export const reduxActions = {
  loadAction,
  receiveAction,
  receiveRemovedAction,
  clearAction,
  newDocumentAction,
  deleteDocumentAction,
  undeleteDocumentAction,
  newCommitAction,
  updateCommitAction,
  updateDocumentAction,
  cancelCommitAction,
  modifyFieldAction,
  modifyLockedFieldAction,
  setNewDocAction,
  unsetNewDocAction,
  loadFilesMetadataAction,
  newFileMetadataAction,
  editFileMetadataAction,
  removeFileMetadataAction,
};
